/**
 * Centralized API Configuration
 * 
 * This utility provides consistent API URL resolution across all components,
 * eliminating duplicate getApiBase() implementations.
 */

import config from '../config';

/**
 * Get the base API URL for the current environment.
 *
 * Delegates to config.js, which selects the environment from the build-time
 * REACT_APP_ENV rather than sniffing the hostname. An explicit REACT_APP_API_URL
 * always wins (config.js already applies it for staging/production; checking it here
 * also preserves the override for development builds).
 * @returns {string} The base API URL
 */
export const getApiBase = () => process.env.REACT_APP_API_URL || config.apiUrl;

/**
 * Get the full API URL for a specific endpoint
 * @param {string} endpoint - The API endpoint path (e.g., '/v2/cloud-run/school/reprocess')
 * @returns {string} The complete API URL
 */
export const getApiUrl = (endpoint) => {
  const baseUrl = getApiBase();
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return `${baseUrl}${cleanEndpoint}`;
};

/**
 * Common API endpoints used across the application
 */
export const API_ENDPOINTS = {
  // Cloud Run service endpoints
  CLOUD_RUN: {
    BASE: '/v2/cloud-run',
    REPROCESS: '/v2/cloud-run/school/reprocess',
    ENHANCED_PROCESS: '/v2/cloud-run/school/process-enhanced',
    BULK_PROCESS: '/v2/cloud-run/school/process-bulk-enhanced',
    STATUS: '/v2/cloud-run/school/status',
    SEARCH: '/v2/cloud-run/school/search',
    HEALTH: '/v2/cloud-run/health'
  },
  
  // Backend service endpoints
  PROJECTIONS: {
    BASE: '/v2/projections',
    PROCESS: '/v2/projections/school',
    TEST: '/v2/projections/test'
  },
  
  METRICS: {
    BASE: '/v2',
    PROCESS: '/v2/process/metrics',
    TEST: '/v2/metrics/test'
  },
  
  ADMIN: {
    BASE: '/v2/admin',
    TEAMS: '/v2/admin/teams'
  }
};

/**
 * Environment detection utilities
 */
export const ENVIRONMENTS = {
  LOCAL: 'local',
  STAGING: 'staging',
  PRODUCTION: 'production'
};

/**
 * Get the current environment
 * @returns {string} Current environment identifier
 */
export const getCurrentEnvironment = () => {
  const hostname = window.location.hostname;
  
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return ENVIRONMENTS.LOCAL;
  } else if (hostname.includes('staging')) {
    return ENVIRONMENTS.STAGING;
  } else if (hostname.includes('production') || hostname === 'educationdatacollective.com' || hostname === 'www.educationdatacollective.com') {
    return ENVIRONMENTS.PRODUCTION;
  } else {
    return 'unknown';
  }
};

/**
 * Debug utility to log API configuration
 */
export const debugApiConfig = () => {
  console.group('API Configuration Debug');
  console.log('Current hostname:', window.location.hostname);
  console.log('Current environment:', getCurrentEnvironment());
  console.log('Base API URL:', getApiBase());
  console.log('Environment variable REACT_APP_API_URL:', process.env.REACT_APP_API_URL);
  console.groupEnd();
};
